import { useEffect, useState } from "react";
import { Modal, ModalProps } from "features";
import { twMerge } from "tailwind-merge";
import { Button, Loading, Checkbox, WithActions } from "components";
import { t } from "i18next";

type RolePermissionsConfirmModalProps = {
  title: string;
  label?: string;
  onSave: () => Promise<void> | void;
  loading: boolean;
  onSuccess?: () => void;
} & ModalProps;

export const RolePermissionsConfirmModal = ({
  title,
  label,
  onSave,
  loading,
  open,
  setOpen,
}: RolePermissionsConfirmModalProps) => {
  const [checked, setChecked] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset states when modal is closed
      setIsSuccess(false);
      setChecked(false);
      setIsSaving(false);
    }
  }, [open]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
      setIsSuccess(true);
    } catch (error) {
      // Error handling can be added here if needed
      console.error("Error saving permissions:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      withActions={false}
      open={open}
      setOpen={setOpen}
      className={twMerge("flex min-h-[370px] max-w-[600px] p-0 text-center")}
    >
      {isSuccess ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-90 p-8">
          <div className="flex w-full max-w-md flex-col items-center justify-center gap-6 text-center">
            <p className="fs-10 text-[23px] font-bold uppercase leading-[1.5] text-[#1C67AD] underline">
              {t("roleManagement.permissionsUpdatedSuccessfullyOfRole", {
                roleName: title
                  .replace("You are about to make changes to the permissions of role ", "")
                  .replace(" has been changed", ""),
              })}
            </p>
          </div>
        </div>
      ) : (
        <div className="relative flex w-full flex-col items-center justify-center gap-6 px-16">
          {loading && <Loading absolute />}
          <h1 className="uppercase">{title}</h1>
          {label && (
            <div className="w-full text-left">
              <Checkbox label={label} onChange={({ target }) => setChecked(target.checked)} checked={checked} />
            </div>
          )}
          {label && (
            <div className="w-full">
              <WithActions closeButton={t("common.back")} setOpen={setOpen}>
                <Button onClick={handleSave} disabled={!checked || isSaving}>
                  {isSaving ? t("common.loading") || "Saving..." : t("common.save") || "Save"}
                </Button>
              </WithActions>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};
